import React from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/Button/Button';
import { Badge } from '@/components/ui/Badge/Badge';
import { SimpleTooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';

interface StepHeaderProps {
  /** Title of the step */
  title: string;
  /** Subtitle/description */
  subtitle?: string;
  /** Go back to previous step */
  onBack: () => void;
  /** Label for the back button */
  backLabel?: string;
  /** Continue to next step */
  onContinue?: () => void;
  /** Label for the continue button */
  continueLabel?: string;
  /** Count badge for continue button */
  continueCount?: number;
  /** Whether continue is disabled */
  continueDisabled?: boolean;
  /** Tooltip for disabled continue button */
  continueDisabledTooltip?: string;
  /** Tooltip for enabled continue button */
  continueTooltip?: string;
  /** Additional content to render in the header (e.g., connection status) */
  children?: React.ReactNode;
  /** Additional classes for the header container */
  className?: string;
}

/**
 * Reusable header component for wizard steps
 * Provides consistent styling with back button, title, subtitle, and continue action
 */
export function StepHeader({
  title,
  subtitle,
  onBack,
  backLabel = 'Back',
  onContinue,
  continueLabel,
  continueCount,
  continueDisabled = false,
  continueDisabledTooltip,
  continueTooltip,
  children,
  className,
}: StepHeaderProps) {
  const showContinueButton = onContinue && continueLabel;
  
  return (
    <div className={cn("border-b bg-white px-4 py-4 sm:px-6", className)}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        {/* Left side: Back button + Title */}
        <div className="flex items-start gap-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-md transition-colors flex-shrink-0 mt-0.5"
            aria-label={backLabel}
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              {title}
            </h1>
            {subtitle && (
              <p className="text-sm text-muted-foreground mt-0.5">
                {subtitle}
              </p>
            )}
          </div>
        </div>
        
        {/* Right side: Continue button */}
        {showContinueButton && (
          <div className="flex items-center gap-2 flex-shrink-0 sm:mt-0.5">
            <SimpleTooltip
              content={
                continueDisabled 
                  ? continueDisabledTooltip 
                  : continueTooltip
              }
            >
              <span>
                <Button
                  variant={continueDisabled ? "outline" : "primary"}
                  size="sm"
                  rightIcon={<ArrowRight className="w-4 h-4" />}
                  onClick={onContinue}
                  disabled={continueDisabled}
                  className={cn(
                    "relative transition-all",
                    !continueDisabled && "shadow-sm hover:shadow-md"
                  )}
                >
                  <span className="hidden sm:inline">{continueLabel}</span>
                  <span className="sm:hidden">{continueLabel.split(' ').pop()}</span>
                  {continueCount !== undefined && continueCount > 0 && (
                    <Badge 
                      variant={continueDisabled ? "secondary" : "primary-inverted"}
                      className="ml-2 px-1.5 py-0 text-xs h-5 min-w-[20px] flex items-center justify-center"
                    >
                      {continueCount}
                    </Badge>
                  )}
                </Button>
              </span>
            </SimpleTooltip>
          </div>
        )}
      </div>
      
      {/* Additional content (connection status, etc.) */}
      {children && (
        <div className="mt-4">
          {children}
        </div>
      )}
    </div>
  );
}

